import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';

export type SidebarRequestResult<Confirm, Close = undefined> =
  | { reason: 'confirm'; data: Confirm }
  | { reason: 'close'; data?: Close };

type SidebarRequestState<Data> = {
  data: Data;
  key: number;
};

type SidebarControls = {
  open: (id: string) => void;
  close: () => Promise<void>;
  getActiveSidebarId: () => string | null;
  setLocked: (id: string, isLocked: boolean) => void;
};

export type SidebarRequest<Data, Confirm, Close = undefined> = {
  request: (data: Data) => Promise<SidebarRequestResult<Confirm, Close>>;
  confirm: (result: Confirm) => void;
  dismiss: (data?: Close) => void;
  handleToggle: (isOpen: boolean) => void;
  setBusy: (isBusy: boolean) => void;
  useRequestState: () => SidebarRequestState<Data> | null;
};

export function createSidebarRequestWith<Data, Confirm, Close = undefined>(
  sidebarId: string,
  sidebar: SidebarControls,
): SidebarRequest<Data, Confirm, Close> {
  const $state = atom<SidebarRequestState<Data> | null>(null);
  let settlePending: ((result: SidebarRequestResult<Confirm, Close>) => void) | null = null;
  let isBusy = false;

  const setBusy = (busy: boolean) => {
    isBusy = busy && settlePending != null;
    sidebar.setLocked(sidebarId, isBusy);
  };

  const settle = (result: SidebarRequestResult<Confirm, Close>) => {
    const pending = settlePending;
    if (pending == null) return;
    settlePending = null;
    setBusy(false);
    pending(result);
    if (sidebar.getActiveSidebarId() === sidebarId) void sidebar.close();
  };

  return {
    request: (data) => {
      if (isBusy) return Promise.resolve({ reason: 'close' });
      settle({ reason: 'close' });
      sidebar.open(sidebarId);
      if (sidebar.getActiveSidebarId() !== sidebarId) return Promise.resolve({ reason: 'close' });
      $state.set({ data, key: ($state.get()?.key ?? 0) + 1 });
      return new Promise((resolve) => {
        settlePending = resolve;
      });
    },
    confirm: (result) => settle({ reason: 'confirm', data: result }),
    dismiss: (data) => settle({ reason: 'close', data }),
    handleToggle: (isOpen) => {
      if (!isOpen) settle({ reason: 'close' });
    },
    setBusy,
    useRequestState: () => useStore($state),
  };
}
